import React, { useEffect, useState } from "react";
import "./index.css";
import { CustomMenu, QuantityMap } from "../../../../../../redux/allocationService";
import { getTierFromTags } from "../../../../../../utility/Utility";
import { ProteinPanel } from "../../ProteinPanel";
import { TierBadge } from "../../../../../../components/TierBadge";

interface CustomMenuRowProps {
  menu: CustomMenu;
  index: number;
  quantities: QuantityMap;
  onQuantityChange: (key: string, value: number) => void;
}

export const CustomMenuRow: React.FC<CustomMenuRowProps> = ({ 
  menu, 
  index, 
  quantities, 
  onQuantityChange 
}) => {
  const [proteinOpen, setProteinOpen] = useState(false);
  const tier = getTierFromTags(menu.tags as any);
  const qtyKey = `custom:${menu.itemId}`;
  const qty = quantities[qtyKey] ?? 0;

  // Auto-open protein panel if any protein quantities are pre-populated
  useEffect(() => {
    const hasPrePopulatedProteins = menu.sections.some((section) =>
      section.menuItems.some((item) => {
        const key = `prot:${menu.itemId}:${item.itemId}`;
        return (quantities[key] ?? 0) > 0;
      })
    );
    if (hasPrePopulatedProteins) {
      setProteinOpen(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Calculate active protein count (number of distinct items with quantity > 0)
  const activeProteinCount = menu.sections.reduce((sTotal, section) => {
    return (
      sTotal +
      section.menuItems.reduce((iTotal, item) => {
        const key = `prot:${menu.itemId}:${item.itemId}`;
        return iTotal + ((quantities[key] ?? 0) > 0 ? 1 : 0);
      }, 0)
    );
  }, 0);

  // Calculate total protein assigned (sum of all quantities)
  const proteinAssigned = menu.sections.reduce((sTotal, section) => {
    return (
      sTotal +
      section.menuItems.reduce((iTotal, item) => {
        const key = `prot:${menu.itemId}:${item.itemId}`;
        return iTotal + (quantities[key] ?? 0);
      }, 0)
    );
  }, 0);

  const hasProteinMismatch = qty > 0 && proteinAssigned !== qty;
  const hasProteinComplete = qty > 0 && proteinAssigned === qty;
  const hasSections = menu.sections.some((s) => s.menuItems.length > 0);

  return (
    <div
      className={`packs-menu-card ${qty > 0 ? "packs-menu-card--active" : ""} packs-menu-card--tier-${tier}`}
    >
      <div className="packs-menu-row packs-menu-row--card-inner">
        <span className="packs-menu-row__num">{index}</span>
        <TierBadge tier={tier} />
        <div className="packs-menu-row__info">
          <span className="packs-menu-row__name">{menu.displayName}</span>
          <div className="packs-menu-row__meta">
            {menu.productId && (
              <span className="packs-menu-row__sku">{menu.productId}</span>
            )}
            {menu.abbreviation && (
              <span className="packs-menu-row__abbr">{menu.abbreviation}</span>
            )}
            <span className="packs-menu-row__category">{menu.category}</span>
          </div>
        </div>

        <div className="packs-menu-row__qty-area">
          <span className="packs-qty-label">QTY</span>
          <input
            className="packs-qty-input"
            type="number"
            min={0}
            placeholder="0"
            value={qty === 0 ? "" : qty}
            onChange={(e) =>
              onQuantityChange(
                qtyKey,
                Math.max(0, parseInt(e.target.value) || 0)
              )
            }
          />
        </div>

        <div className="packs-menu-row__totals">
          <div className="packs-total-pill" data-filled={qty > 0}>
            <span className="packs-total-pill__label">TOTAL</span>
            <span className="packs-total-pill__num">{qty}</span>
          </div>

          {hasSections && (
            <div
              className={`packs-protein-pill ${
                hasProteinComplete
                  ? "packs-protein-pill--complete"
                  : hasProteinMismatch
                  ? "packs-protein-pill--warn"
                  : ""
              }`}
            >
              <span className="packs-protein-pill__label">PROTEIN</span>
              <span className="packs-protein-pill__num">
                {proteinAssigned} / {qty}
              </span>
            </div>
          )}
        </div>

        {hasSections && (
          <button
            className={`packs-proteins-btn ${proteinOpen ? "packs-proteins-btn--open" : ""} packs-proteins-btn--tier-${tier}`}
            onClick={() => setProteinOpen((v) => !v)}
          >
            {proteinOpen
              ? "Hide Proteins"
              : `Proteins${activeProteinCount > 0 ? ` (${activeProteinCount})` : ""}`}
          </button>
        )}
      </div>

      {hasSections && proteinOpen && (
        <ProteinPanel 
          menu={menu} 
          packTotal={qty}
          quantities={quantities}
          onQuantityChange={onQuantityChange}
        />
      )}
    </div>
  );
};